﻿import { ObjectUtils, StringUtils } from '../main/ObjectUtils.js';
import { ProductMaterialsMap } from './ProductMaterialsMap.js';
import { ProductInteraction } from './ProductInteraction.js';
import { Component } from './Components.js';
import { WindowResizeMonitor } from '../main/WindowResizeMonitor.js';

export class ProductDrawing {
  constructor(drawingEngine, finishImagesBaseUrl, allFinishesMap) {
    this.interactionBehaviors = [];
    this.isInteractive = false;
    this.isProductDisplayed = false;
    this._product = null;
    this.allFinishesMap = allFinishesMap;
    const getFinishName = this.allFinishesMap
      ? id => this.getProductFinishName(id)
      : id => id;
    this.engine = drawingEngine;
    this.engine.getFinishName = getFinishName;
    this.engine.finishImagesBaseUrl = finishImagesBaseUrl;
    this.drawingService = this.engine.createDrawingService();
    if (ProductMaterialsMap) {
      const cmScale = 1; //10; // Convert from cm to mm
      this.drawingService.addMaterials(ProductMaterialsMap, cmScale);
    } else {
      this.drawingService.addFallbackMaterials();
    }

    /* The bestFitStageAndCanvas method will make the canvas the size of the parent container, and
     * then make the stage as large as possible within the canvas while maintaining it's aspect
     * ratio. When fitCanvasAroundStage is true, the canvas size will be reduced to just fit around
     * the stage, and be positioned in the top/left corner of the parent container. */
    this.fitCanvasAroundStage = false;

    this.resizeMonitor = new WindowResizeMonitor(/*startImmediately:*/ true);
    this.resizeMonitor.onResize = mon => {
      if (this.isProductDisplayed) {
        this.bestFitStageAndCanvas(true);
        /* This handles some weird case where the container's clientWidth isn't updated in real time,
         * but instead happens 300-1000 ms. later. */
        this.doIfCanvasContainerWidthUpdatesWithin(1000, () => {
          //console.log('Canvas container width did update!')
          this.bestFitStageAndCanvas(true);
        });
      }
    };
  }

  get product() {
    return this._product;
  }

  set product(value) {
    if (value !== this._product) {
      this._product = value;
      this.interactionBehaviors.forEach(behavior => {
        if (behavior instanceof ProductInteraction) {
          behavior.product = value;
        }
      });
    }
  }

  getProductFinishName(id) {
    return this.allFinishesMap[id] ? this.allFinishesMap[id].caption.trim() : null;
  }

  async loadMaterials(materialIdsToLoad, loadHighResAsWell) {
    let resolution = this.engine.lowRes;
    //console.log(`Awaiting engine.loadMaterials (${resolution})...`);
    const loadingMaterials = await this.engine.loadMaterials(this.drawingService.materials,
      materialIdsToLoad, resolution);
    //console.log(`${loadingMaterials.length} ${resolution} materials loaded!`);
    if (loadHighResAsWell) {
      resolution = this.engine.highRes;
      // Load the high-res images asynchronously:
      //console.log(`Async engine.loadMaterials (${resolution})...`);
      this.engine.loadMaterials(this.drawingService.materials, materialIdsToLoad, resolution)
        .then(materials => {
          //console.log(`${materials.length} ${resolution} materials async-loaded!`);
          if (materials.length > 0) {
            //console.log('Next, setting current material and refreshing drawing...');
            this.engine.setCurrentMaterialImageResolution(materials, resolution);
            this.drawingService.refreshMaterialImages(this.product);
            //console.log('Current material set and drawing refreshed!');
          }
        });
      //console.log(`Continuing while ${resolution} materials are being loaded...`);
    }
    return loadingMaterials;
  }

  toggleFinishLabels(on) {
    this.isLabelsVisible = on;
    if (this.product) {
      this.product.toggleFinishLabels(on);
    }
    this.engine.renderOnce();
  }

  removeProduct() {
    if (ObjectUtils.isAssigned(this.product)) {
      if (this.isInteractive) {
        /* Since we're just about to remove our product (the displayObjects) from the drawing,
         * let's take the opportunity and deactivate interaction on the displayObjects. */
        this.activateInteraction(false);
      }
      this.product.removeDisplayObject();
      console.log('ProductDrawing: Removed display object from root product');
      if (this.engine.usesStageProduct) {
        this.stageProduct.removeComponent(this.product);
        this.stageProduct = null;
      }
      this.product = null;
      console.log('ProductDrawing: Cleared root product');
    }
  }

  getModifiedApiProduct() {
    return this.product != null ? this.product.getModifiedApiProduct() : null;
  }

  displayProduct(product) {
    const wereLabelsActive = this.isLabelsVisible;
    if (wereLabelsActive) {
      this.toggleFinishLabels(false);
    }
    this.removeProduct();

    // Remove any offsets on the root api product:
    if (product.apiProduct.l > 0) {
      console.log('Product contains location offsets (should have been cleared by server)');
      // 3/28/2019: Clearing the location properties should now have been done in the StoreApi on the Designer server.
      // DONE: Clear the two statements below in a few days (after 3/28/19)
      //product.apiProduct.l = 0;
      //product.apiProduct.b = 0;
    }

    if (this.engine.usesStageProduct) {
      // Create a wrapper component for the stage displayObject, just for convenience reasons:
      const stageApiProduct = {
        l: 0,
        b: 0,
        z: 0,
        w: product.apiProduct.w,
        h: product.apiProduct.h,
        d: product.apiProduct.d,
      };
      this.stageProduct = new Component('Stage Product', this.drawingService, stageApiProduct);
      this.stageProduct.displayObject = this.engine.getStageDisplayObject();
      this.stageProduct.addComponent(product);
    }
    this.product = product;
    // This triggers creation of display objects for the entire product components graph:
    const displayObject = this.drawingService.createDisplayObject(this.product);
    /* This method will add the display object to the parent div (HtmlDrawingEngine). For Pixi and
     * Babylon engines this method might not do anything since the display object (a mesh) has already
     * been added when it was created. */
    this.engine.addToDrawing(displayObject);
    console.log('ProductDrawing: Added root product to the drawing');

    if (this.isInteractive) {
      /* Since we just added our product (the displayObjects) to the drawing, this is the first
       * opportunity to activate interaction on the displayObjects (even if isInteractive is
       * already true). */
      this.activateInteraction(true);
    }

    if (wereLabelsActive) {
      this.toggleFinishLabels(true);
    }

    // TODO: We probably don't need to call renderOnce here since it'll happen inside bestFitStageAndCanvas
    this.engine.renderOnce();
    this.bestFitStageAndCanvas(true);

    if (this.onProductDisplayed) {
      this.onProductDisplayed(this, product);
    }
    this.isProductDisplayed = true;
  }

  //#region Interaction Behaviors

  /**
   * Adds the interaction behavior to the behaviors array and updates the behavior's state based
   * on the value of this.isInteractive.
   */
  addInteractionBehavior(behavior) {
    behavior.onInteraction = b => this.engine.renderOnce();
    this.interactionBehaviors.push(behavior);
    behavior.setIsInteractive(this.isInteractive);
    if (behavior instanceof ProductInteraction) {
      behavior.product = this.product;
    }
  }

  /**
   * Turns interaction off on the behavior and removes the behavior from our array.
   */
  removeInteractionBehavior(behavior) {
    behavior.setIsInteractive(false);
    behavior.onInteraction = null;
    ObjectUtils.removeFromArray(this.interactionBehaviors, behavior);
    if (behavior instanceof ProductInteraction) {
      behavior.product = null;
    }
  }

  /**
   * This method is has protected privacy and is intended to be called when isInteractive is already
   * true, but we haven't yet been able to enable the interaction on the displayObjects (e.g. because
   * the displayObjects were just now created).
   * @param {any} on Activate? or deactivate?
   */
  activateInteraction(on) {
    if (this.isInteractive) {
      this.interactionBehaviors.forEach(b => {
        b.activateInteraction(on);
      });
    }
  }

  /**
   * Sets the isInteractive property value after calling setIsInteractive on each behavior in
   * our array.
   */
  setIsInteractive(on) {
    if (on !== this.isInteractive) {
      this.interactionBehaviors.forEach(b => {
        b.setIsInteractive(on);
      });
      this.isInteractive = on;
    }
  }

  // High end methods:

  removeProductInteraction() {
    if (ObjectUtils.isAssigned(this.productInteraction)) {
      this.removeInteractionBehavior(this.productInteraction);
      this.productInteraction = null;
    }
  }

  addProductInteraction(productInteraction) {
    this.productInteraction = productInteraction;
    this.addInteractionBehavior(productInteraction);
  }

  //#endregion Interaction Behaviors

  /**
   * Returns an object with the width and height of the drawing. This should match the size of the
   * image returned by the getDrawingImageUrl method.
   */
  getDrawingSize() {
    return this.engine.getDrawingSize();
  }

  getDrawingImageUrl() {
    return this.engine.getDrawingImageUrl();
  }

  doIfCanvasContainerWidthUpdatesWithin(ms, callback) {
    const startTime = Date.now();
    const container = this.engine.productDrawing.parentNode;
    const initialWidth = container.clientWidth;
    const onFrame = () => {
      const width = container.clientWidth;
      if (width !== initialWidth) {
        callback();
      } else if (Date.now() - startTime < ms) {
        //console.log('Checking to see if canvas container resizes....requesting another frame...');
        requestAnimationFrame(onFrame);
      } else {
        //console.log('Timed out requesting frames!');
      }
    };
    requestAnimationFrame(onFrame);
  }

  bestFitStageAndCanvas(renderOnce) {
    this.engine.bestFitStageAndCanvas(this.fitCanvasAroundStage, renderOnce);
  }

  resizeCanvasToFitInFullWindow(renderOnce) {
    this.engine.resizeCanvasToFitInFullWindow(renderOnce);
  }

  // As of 8/17/2020 not in use, but should still be functional
  resizeCanvasToFitInFullScreen(renderOnce) {
    this.engine.resizeCanvasToFitInFullScreen(renderOnce);
  }

  resizeCanvasToFitAroundStage(renderOnce) {
    this.engine.resizeCanvasToFitAroundStage(renderOnce);
  }

}
